import { IconArrowDown, IconArrowUp, IconGripVertical, IconRotateClockwise } from '@tabler/icons-react';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type {
  AddressAnswerValue,
  DateTimeAnswerValue,
  ImageClickMapAnswerValue,
  QuestionStructuredValue,
  ReadableSignatureAnswerValue,
  SignatureAnswerValue,
  SignaturePoint,
} from '../../lib/question-blocks';
import {
  normalizeSignatureAnswer,
  signatureSignerName,
  signatureCanvasViewBox,
  signaturePointCount,
  signatureStrokePath,
  validateSignatureSubmission,
} from '../../lib/question-blocks';
import type { RespondentField } from '../../lib/multimodal-contract';
import { ImageClickMapControl } from './ImageClickMapControl';
import { MatrixQuestionControl } from './MatrixQuestionControl';
import './StructuredQuestionControl.css';
import { DateTimeQuestionControl } from './DateTimeQuestionControl';

/** Props shared by the live conversation, correction editor and local preview. */
export interface StructuredQuestionControlProps {
  field: RespondentField;
  value?: QuestionStructuredValue;
  onChange: (value: QuestionStructuredValue | undefined) => void;
  onSubmit: (value: QuestionStructuredValue) => void;
  disabled: boolean;
  labelledBy: string;
  submitLabel?: string;
  /** Corrections stage a single visual choice until the respondent saves it. */
  submitOnSingleSelection?: boolean;
}

const focus = 'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-volt';
const input = `${focus} min-h-11 w-full rounded-lg border border-border bg-transparent px-3 py-2 text-base text-foreground disabled:opacity-50`;
const option = `${focus} choice-control cursor-pointer rounded-lg text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50`;
const action = `${focus} inline-flex cursor-pointer items-center justify-center rounded-lg px-4 py-3 text-sm disabled:cursor-not-allowed disabled:opacity-50`;
const compactAction = `${focus} inline-flex min-h-9 cursor-pointer items-center justify-center rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-50`;
const compactIconAction = `${focus} h-9 w-9 cursor-pointer rounded-lg text-muted-foreground hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-50`;

function isAddress(value: QuestionStructuredValue | undefined): value is AddressAnswerValue {
  return !!value && typeof value === 'object' && 'line1' in value && 'city' in value && 'country' in value;
}

function isDateTime(value: QuestionStructuredValue | undefined): value is DateTimeAnswerValue {
  return !!value && typeof value === 'object' && 'value' in value;
}

function isClickMap(value: QuestionStructuredValue | undefined): value is ImageClickMapAnswerValue {
  return !!value && typeof value === 'object' && 'points' in value;
}

function readSignatureDraft(value: QuestionStructuredValue | undefined): ReadableSignatureAnswerValue | undefined {
  if (value && typeof value === 'object' && 'version' in value && value.version === 2 && 'kind' in value
    && (value.kind === 'typed' || value.kind === 'drawn')) return value as ReadableSignatureAnswerValue;
  return normalizeSignatureAnswer(value);
}

/** One accessible control renderer for all structured question blocks. */
export function StructuredQuestionControl({ field, value, onChange, onSubmit, disabled, labelledBy,
  submitLabel = 'Send', submitOnSingleSelection = true }: StructuredQuestionControlProps): React.ReactElement | null {
  const [draft, setDraft] = useState<QuestionStructuredValue | undefined>(value);
  const [brokenImages, setBrokenImages] = useState<Set<string>>(() => new Set());
  const [availableImages, setAvailableImages] = useState<Set<string>>(() => new Set());
  const [retryingImages, setRetryingImages] = useState<Set<string>>(() => new Set());
  const [signatureMode, setSignatureMode] = useState<'drawn' | 'typed'>(() => normalizeSignatureAnswer(value)?.kind ?? 'drawn');
  const [signatureDrafts, setSignatureDrafts] = useState<{
    drawn?: Extract<ReadableSignatureAnswerValue, { kind: 'drawn' }>;
    typed?: Extract<SignatureAnswerValue, { kind: 'typed' }>;
  }>(() => {
    const initial = normalizeSignatureAnswer(value);
    return initial ? { [initial.kind]: initial } : {};
  });
  const [signatureName, setSignatureName] = useState(() => signatureSignerName(value) ?? '');
  const [liveStroke, setLiveStroke] = useState<SignaturePoint[]>([]);
  const [liveCanvas, setLiveCanvas] = useState<{ width: number; height: number } | undefined>();
  const [signatureLimitReached, setSignatureLimitReached] = useState(false);
  const controlId = useId();
  const draggedRankingId = useRef<string | null>(null);
  const rankingRows = useRef(new Map<string, HTMLLIElement>());
  const rankingRects = useRef(new Map<string, DOMRect>());
  const rankingFieldId = useRef(field.fieldId);
  const drawing = useRef<{ pointerId: number; points: SignaturePoint[]; canvas: { width: number; height: number } } | null>(null);
  const signatureFieldId = useRef(field.fieldId);
  const typedSignatureInput = useRef<HTMLInputElement>(null);
  const focusTypedSignature = useRef(false);
  const imageIdentity = field.type === 'image_choice'
    ? (field.options ?? []).map(item => `${item.id}:${item.image?.imageUrl ?? ''}`).join('|')
    : '';

  useEffect(() => {
    setDraft(value);
    const nextSignature = normalizeSignatureAnswer(value);
    if (signatureFieldId.current !== field.fieldId) {
      signatureFieldId.current = field.fieldId;
      setSignatureDrafts(nextSignature ? { [nextSignature.kind]: nextSignature } : {});
      setSignatureName(signatureSignerName(nextSignature) ?? '');
    } else if (nextSignature) {
      setSignatureDrafts(current => ({ ...current, [nextSignature.kind]: nextSignature }));
    }
    if (nextSignature) setSignatureMode(nextSignature.kind);
  }, [field.fieldId, value]);

  useEffect(() => {
    setBrokenImages(new Set());
    setAvailableImages(new Set());
    setRetryingImages(new Set());
  }, [field.fieldId, imageIdentity]);

  useEffect(() => {
    if (signatureMode === 'typed' && focusTypedSignature.current) {
      typedSignatureInput.current?.focus();
      focusTypedSignature.current = false;
    }
  }, [signatureMode]);

  useLayoutEffect(() => {
    if (field.type !== 'ranking') return;
    const nextRects = new Map<string, DOMRect>();
    rankingRows.current.forEach((element, id) => nextRects.set(id, element.getBoundingClientRect()));
    if (rankingFieldId.current === field.fieldId
      && !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      nextRects.forEach((nextRect, id) => {
        const previousRect = rankingRects.current.get(id);
        const deltaY = previousRect ? previousRect.top - nextRect.top : 0;
        const element = rankingRows.current.get(id);
        if (deltaY && element?.animate) {
          element.animate([
            { transform: `translateY(${deltaY}px)` },
            { transform: 'translateY(0)' },
          ], { duration: 300, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' });
        }
      });
    }
    rankingFieldId.current = field.fieldId;
    rankingRects.current = nextRects;
  }, [draft, field.fieldId, field.type]);

  const update = (next: QuestionStructuredValue | undefined): void => {
    setDraft(next);
    onChange(next);
  };
  const submit = (next = draft): void => {
    if (next !== undefined) onSubmit(next);
  };
  const prepareRankingMove = (): void => {
    const currentRects = new Map<string, DOMRect>();
    rankingRows.current.forEach((element, id) => {
      currentRects.set(id, element.getBoundingClientRect());
      element.getAnimations?.().forEach(animation => animation.cancel());
    });
    rankingRects.current = currentRects;
  };
  const submitButton = (valid: boolean, label = submitLabel, submittedValue?: QuestionStructuredValue) => (
    <button type="button" className={`${action} button-accent`} disabled={disabled || !valid}
      onClick={() => submit(submittedValue)}>{label}</button>
  );

  if (field.type === 'checkbox') {
    const checked = draft === true;
    return <div className="space-y-4">
      <label className={`${option} flex min-h-11 items-center gap-3 px-4 py-3 text-sm`}>
        <input type="checkbox" name={field.fieldId} checked={checked} disabled={disabled} aria-label={field.label} aria-labelledby={labelledBy}
          className={`h-5 w-5 accent-volt ${focus}`} onChange={event => update(event.target.checked)} />
        <span aria-hidden="true">Confirm</span>
      </label>
      {field.checkbox?.link ? <a href={field.checkbox.link.url} target="_blank" rel="noreferrer"
        className={`${focus} inline-block text-sm text-muted-foreground underline underline-offset-4`}>{field.checkbox.link.label}</a> : null}
      {submitButton(!field.required || checked, submitLabel, checked)}
    </div>;
  }

  if (field.type === 'image_choice') {
    const options = field.options ?? [];
    const selected = Array.isArray(draft) ? draft.filter((item): item is string => typeof item === 'string') : [];
    const multiple = field.selectionMode === 'multiple';
    const min = field.minSelected ?? 1;
    const max = multiple ? field.maxSelected ?? options.length : 1;
    const choose = (id: string): void => {
      const next = multiple ? selected.includes(id) ? selected.filter(item => item !== id)
        : selected.length < max ? [...selected, id] : selected : [id];
      update(next);
      if (!multiple && submitOnSingleSelection) onSubmit(next);
    };
    return <div className="space-y-4">
      <div role="group" aria-labelledby={labelledBy} className="structured-image-choice-grid">
        {options.map((item, index) => {
          const selectedItem = selected.includes(item.id);
          const broken = brokenImages.has(item.id);
          const retrying = retryingImages.has(item.id);
          const unavailable = Boolean(item.image && (broken || !availableImages.has(item.id)));
          const inputId = `${controlId}-image-${index}`;
          return <div key={item.id} data-selected={selectedItem} className="choice-control structured-image-choice-tile overflow-hidden rounded-lg">
            {item.image ? <div className="aspect-[4/3] overflow-hidden bg-surface">
              {!broken || retrying ? <label htmlFor={inputId} className={`block h-full w-full ${disabled || unavailable ? 'cursor-default' : 'cursor-pointer'}`}>
                <img src={item.image.imageUrl} alt={item.image.alt} width="4" height="3" className="h-full w-full object-cover"
                onLoad={() => {
                  setAvailableImages(previous => new Set(previous).add(item.id));
                  setBrokenImages(previous => { const next = new Set(previous); next.delete(item.id); return next; });
                  setRetryingImages(previous => { const next = new Set(previous); next.delete(item.id); return next; });
                }}
                onError={() => {
                  setAvailableImages(previous => { const next = new Set(previous); next.delete(item.id); return next; });
                  setBrokenImages(previous => new Set(previous).add(item.id));
                  setRetryingImages(previous => { const next = new Set(previous); next.delete(item.id); return next; });
                  if (selectedItem) update(selected.filter(selectedId => selectedId !== item.id));
                }} />
              </label>
                : <div className="structured-image-choice-unavailable flex h-full flex-col items-start justify-center gap-2 px-4 text-sm text-muted-foreground">
                  <span>Image unavailable</span>
                  <button type="button" aria-label={`Retry ${item.label} image`} className={`${focus} cursor-pointer underline`}
                    onClick={() => setRetryingImages(previous => new Set(previous).add(item.id))}>Retry</button>
                </div>}
            </div> : null}
            <label className={`${option} flex min-h-11 items-center gap-3 px-3 py-3`}>
              <input id={inputId} type={multiple ? 'checkbox' : 'radio'} name={`image-choice-${field.fieldId}`} checked={selectedItem}
                aria-label={item.label}
                disabled={disabled || unavailable || (multiple && !selectedItem && selected.length >= max)} className={`h-4 w-4 accent-volt ${focus}`}
                onChange={() => choose(item.id)} />
              <span className="structured-image-choice-label min-w-0 break-words text-sm">{item.label}</span>
            </label>
          </div>;
        })}
      </div>
      {multiple || !submitOnSingleSelection ? submitButton(selected.length >= min && selected.length <= max) : null}
    </div>;
  }

  if (field.type === 'ranking') {
    const options = field.options ?? [];
    const optionIds = options.map(item => item.id);
    const order = Array.isArray(draft) && draft.length === optionIds.length
      ? draft.filter((item): item is string => typeof item === 'string') : optionIds;
    const move = (from: number, to: number): void => {
      if (to < 0 || to >= order.length || from === to) return;
      prepareRankingMove();
      const next = [...order];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      update(next);
    };
    return <div className="space-y-4">
      <ol aria-labelledby={labelledBy} className="structured-ranking space-y-2">
        {order.map((id, index) => {
          const item = options.find(candidate => candidate.id === id)!;
          return <li key={id} ref={element => {
            if (element) rankingRows.current.set(id, element);
            else rankingRows.current.delete(id);
          }} draggable={!disabled} onDragStart={() => { draggedRankingId.current = id; }}
            onDragOver={event => event.preventDefault()} onDrop={() => {
              const from = order.indexOf(draggedRankingId.current ?? '');
              draggedRankingId.current = null;
              if (from >= 0) move(from, index);
            }} className="structured-ranking-row min-h-11 rounded-lg bg-surface px-3">
            <IconGripVertical aria-hidden="true" className="h-5 w-5 shrink-0 text-muted-foreground" />
            <span className="structured-ranking-label min-w-0 break-words text-sm"><span className="mr-2 text-muted-foreground">{index + 1}</span>{item.label}</span>
            <span className="structured-ranking-actions">
              <button type="button" aria-label={`Move ${item.label} up`} disabled={disabled || index === 0}
                className={`${focus} h-9 w-9 cursor-pointer rounded-lg hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-30`} onClick={() => move(index, index - 1)}><IconArrowUp aria-hidden="true" className="mx-auto h-4 w-4" /></button>
              <button type="button" aria-label={`Move ${item.label} down`} disabled={disabled || index === order.length - 1}
                className={`${focus} h-9 w-9 cursor-pointer rounded-lg hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-30`} onClick={() => move(index, index + 1)}><IconArrowDown aria-hidden="true" className="mx-auto h-4 w-4" /></button>
            </span>
          </li>;
        })}
      </ol>
      {submitButton(order.length === optionIds.length && order.length > 0,
        submitLabel === 'Send' ? 'Send order' : submitLabel, order)}
    </div>;
  }

  if (field.type === 'date_time') {
    if (!field.dateTime) return <p role="alert" className="text-sm text-danger">Date control unavailable</p>;
    return <DateTimeQuestionControl key={`${field.fieldId}:${field.dateTime.mode}`} config={field.dateTime} bounds={field.dateBounds} value={isDateTime(draft) ? draft : undefined}
      onChange={update} onSubmit={onSubmit} disabled={disabled} labelledBy={labelledBy} submitLabel={submitLabel} />;
  }

  if (field.type === 'matrix') {
    const matrix = field.matrix;
    if (!matrix) return <p role="alert" className="text-sm text-danger">Matrix unavailable</p>;
    const current = draft && typeof draft === 'object' && 'selections' in draft ? draft : { selections: {} };
    return <MatrixQuestionControl fieldId={field.fieldId} matrix={matrix} selectionMode={field.selectionMode} value={current}
      onChange={update} onSubmit={submit} disabled={disabled} labelledBy={labelledBy} submitLabel={submitLabel} />;
  }

  if (field.type === 'address') {
    const address = field.address;
    if (!address) return <p role="alert" className="text-sm text-danger">Address control unavailable</p>;
    const current: AddressAnswerValue = isAddress(draft) ? draft : { line1: '', city: '', country: '' };
    const set = (key: keyof AddressAnswerValue, next: string): void => {
      const changed = { ...current, [key]: next };
      if ((key === 'line2' || key === 'region' || key === 'postalCode') && !next.trim()) delete changed[key];
      update(changed);
    };
    const cleaned: AddressAnswerValue = { line1: current.line1, city: current.city, country: current.country,
      ...(current.line2?.trim() ? { line2: current.line2 } : {}), ...(current.region?.trim() ? { region: current.region } : {}),
      ...(current.postalCode?.trim() ? { postalCode: current.postalCode } : {}) };
    const valid = Boolean(current.line1.trim() && current.city.trim() && current.country.trim()
      && (!address.requirePostalCode || current.postalCode?.trim()));
    return <div className="structured-address space-y-3">
      <label className="grid min-w-0 gap-1 text-sm text-muted-foreground">Address line 1<input name={`${field.fieldId}-line1`} aria-label="Address line 1" autoComplete="address-line1" value={current.line1} disabled={disabled} className={input} onChange={event => set('line1', event.target.value)} /></label>
      <label className="grid min-w-0 gap-1 text-sm text-muted-foreground">Address line 2 (optional)<input name={`${field.fieldId}-line2`} aria-label="Address line 2" autoComplete="address-line2" value={current.line2 ?? ''} disabled={disabled} className={input} onChange={event => set('line2', event.target.value)} /></label>
      <div className="structured-address-grid">
        <label className="grid min-w-0 gap-1 text-sm text-muted-foreground">City<input name={`${field.fieldId}-city`} aria-label="City" autoComplete="address-level2" value={current.city} disabled={disabled} className={input} onChange={event => set('city', event.target.value)} /></label>
        <label className="grid min-w-0 gap-1 text-sm text-muted-foreground">Region (optional)<input name={`${field.fieldId}-region`} aria-label="Region" autoComplete="address-level1" value={current.region ?? ''} disabled={disabled} className={input} onChange={event => set('region', event.target.value)} /></label>
        <label className="grid min-w-0 gap-1 text-sm text-muted-foreground">Postal code{address.requirePostalCode ? '' : ' (optional)'}<input name={`${field.fieldId}-postal-code`} aria-label="Postal code" autoComplete="postal-code" required={address.requirePostalCode} value={current.postalCode ?? ''} disabled={disabled} className={input} onChange={event => set('postalCode', event.target.value)} /></label>
        <label className="grid min-w-0 gap-1 text-sm text-muted-foreground">Country<input name={`${field.fieldId}-country`} aria-label="Country" autoComplete="country-name" value={current.country} disabled={disabled} className={input} onChange={event => set('country', event.target.value)} /></label>
      </div>
      {submitButton(valid, submitLabel, cleaned)}
    </div>;
  }

  if (field.type === 'signature') {
    const signature = readSignatureDraft(draft);
    const drawnSignature = signature?.kind === 'drawn' ? signature : signatureDrafts.drawn;
    const typedCandidate: Extract<SignatureAnswerValue, { kind: 'typed' }> = {
      version: 2, kind: 'typed', typedName: signatureName,
    };
    const typedValidation = validateSignatureSubmission(typedCandidate);
    const validTypedSignature = typedValidation.valid && typedValidation.value.kind === 'typed'
      ? typedValidation.value : undefined;
    const drawnCandidate = drawnSignature ? { ...drawnSignature, signerName: signatureName } : undefined;
    const drawnValidation = validateSignatureSubmission(drawnCandidate);
    const validDrawnSignature = drawnValidation.valid && drawnValidation.value.kind === 'drawn'
      ? drawnValidation.value : undefined;
    const strokes = drawnSignature?.strokes ?? [];
    const storedPointCount = signaturePointCount(drawnSignature);
    const point = (element: HTMLElement, clientX: number, clientY: number): SignaturePoint | undefined => {
      const rect = element.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return undefined;
      return [
        Math.round(Math.max(0, Math.min(1, (clientX - rect.left) / rect.width)) * 10_000),
        Math.round(Math.max(0, Math.min(1, (clientY - rect.top) / rect.height)) * 10_000),
      ];
    };
    const finishStroke = (): void => {
      if (drawing.current?.points.length) {
        const raw: Extract<SignatureAnswerValue, { kind: 'drawn' }> = {
          version: 2,
          kind: 'drawn',
          signerName: signatureName,
          encoding: 'normalized-10000',
          canvas: drawnSignature?.canvas ?? drawing.current.canvas,
          strokes: [...strokes, drawing.current.points],
        };
        const normalized = normalizeSignatureAnswer(raw);
        const next = normalized?.kind === 'drawn' ? normalized : raw;
        setSignatureDrafts(current => ({ ...current, drawn: next }));
        update(next);
      }
      drawing.current = null;
      setLiveStroke([]);
      setLiveCanvas(undefined);
    };
    const switchMode = (mode: 'drawn' | 'typed'): void => {
      setSignatureMode(mode);
      if (mode === 'typed') {
        setSignatureDrafts(current => ({ ...current, typed: typedCandidate }));
        update(typedCandidate);
      } else {
        const next = signatureDrafts.drawn
          ? { ...signatureDrafts.drawn, signerName: signatureName }
          : undefined;
        if (next) setSignatureDrafts(current => ({ ...current, drawn: next }));
        update(next);
      }
    };
    const clear = (): void => {
      setSignatureDrafts(current => ({ ...current, [signatureMode]: undefined }));
      if (signatureMode === 'typed') setSignatureName('');
      setSignatureLimitReached(false);
      update(undefined);
    };
    const undo = (): void => {
      if (!drawnSignature) return;
      const remaining = drawnSignature.strokes.slice(0, -1);
      const next = remaining.length ? { ...drawnSignature, strokes: remaining } : undefined;
      setSignatureDrafts(current => ({ ...current, drawn: next }));
      setSignatureLimitReached(false);
      update(next);
    };
    const previewCanvas = drawnSignature?.canvas ?? liveCanvas ?? { width: 3, height: 1 };
    const previewSignature: Extract<SignatureAnswerValue, { kind: 'drawn' }> = {
      version: 2,
      kind: 'drawn',
      signerName: signatureName,
      encoding: 'normalized-10000',
      canvas: previewCanvas,
      strokes: [],
    };
    const updateSignatureName = (nextName: string): void => {
      setSignatureName(nextName);
      if (signatureMode === 'typed') {
        const next: Extract<SignatureAnswerValue, { kind: 'typed' }> = { version: 2, kind: 'typed', typedName: nextName };
        setSignatureDrafts(current => ({ ...current, typed: next }));
        update(next);
      } else if (drawnSignature) {
        const raw: Extract<SignatureAnswerValue, { kind: 'drawn' }> = { ...drawnSignature, signerName: nextName };
        const normalized = normalizeSignatureAnswer(raw);
        const next = normalized?.kind === 'drawn' ? normalized : raw;
        setSignatureDrafts(current => ({ ...current, drawn: next }));
        update(next);
      } else {
        update(undefined);
      }
    };
    return <div className="space-y-4">
      <div className="structured-signature-track" role="group" aria-label="Signature method">
        <span data-testid="signature-mode-indicator" aria-hidden="true" className="structured-signature-indicator"
          style={{ transform: `translateX(${signatureMode === 'drawn' ? 0 : 100}%)` }} />
        <button type="button" aria-pressed={signatureMode === 'drawn'} disabled={disabled}
          className={`${focus} structured-signature-mode-button hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50`}
          onClick={() => switchMode('drawn')}>Draw</button>
        <button type="button" aria-pressed={signatureMode === 'typed'} disabled={disabled}
          className={`${focus} structured-signature-mode-button hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50`}
          onClick={() => switchMode('typed')}>Type</button>
      </div>
      <div data-testid="signature-stage" className="structured-signature-stage"
        style={{ aspectRatio: `${previewCanvas.width} / ${previewCanvas.height}` }}>
        <div role="application" aria-label="Draw signature" aria-description="Press Enter to type your name instead"
          aria-hidden={signatureMode !== 'drawn'} data-active={signatureMode === 'drawn'}
          tabIndex={!disabled && signatureMode === 'drawn' ? 0 : -1}
          className={`${focus} structured-signature-panel structured-signature-drawn relative w-full touch-none overflow-hidden rounded-lg border border-border bg-background`}
          onKeyDown={event => {
            if (!disabled && signatureMode === 'drawn' && (event.key === 'Enter' || event.key === ' ')) {
              event.preventDefault();
              focusTypedSignature.current = true;
              switchMode('typed');
            }
          }}
          onPointerDown={event => {
            if (disabled || signatureMode !== 'drawn' || drawing.current || strokes.length >= 64 || storedPointCount >= 4096) return;
            const rect = event.currentTarget.getBoundingClientRect();
            const first = point(event.currentTarget, event.clientX, event.clientY);
            if (!first || rect.width <= 0 || rect.height <= 0) return;
            event.currentTarget.setPointerCapture?.(event.pointerId);
            const canvas = drawnSignature?.canvas ?? {
              width: Math.min(10_000, Math.max(1, Math.round(rect.width))),
              height: Math.min(10_000, Math.max(1, Math.round(rect.height))),
            };
            drawing.current = { pointerId: event.pointerId, points: [first], canvas };
            setLiveCanvas(canvas);
            setLiveStroke(drawing.current.points);
          }}
          onPointerMove={event => {
            if (!drawing.current || drawing.current.pointerId !== event.pointerId) return;
            if (disabled || signatureMode !== 'drawn' || storedPointCount + drawing.current.points.length >= 4096) {
              drawing.current = null;
              setLiveStroke([]);
              setLiveCanvas(undefined);
              setSignatureLimitReached(true);
              return;
            }
            const next = point(event.currentTarget, event.clientX, event.clientY);
            if (!next) return;
            const previous = drawing.current.points.at(-1);
            if (previous?.[0] === next[0] && previous[1] === next[1]) return;
            drawing.current = { ...drawing.current, points: [...drawing.current.points, next] };
            setLiveStroke(drawing.current.points);
          }}
          onPointerUp={event => {
            if (!drawing.current || drawing.current.pointerId !== event.pointerId) return;
            event.currentTarget.releasePointerCapture?.(event.pointerId);
            if (disabled || signatureMode !== 'drawn') {
              drawing.current = null;
              setLiveStroke([]);
              setLiveCanvas(undefined);
              return;
            }
            finishStroke();
          }}
          onPointerCancel={event => {
            if (!drawing.current || drawing.current.pointerId !== event.pointerId) return;
            drawing.current = null;
            setLiveStroke([]);
            setLiveCanvas(undefined);
          }}>
          <svg aria-hidden="true" viewBox={signatureCanvasViewBox(previewSignature)} className="h-full w-full">
            {strokes.map((stroke, index) => <path key={index} d={signatureStrokePath(previewSignature, stroke)}
              fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />)}
            {liveStroke.length ? <path d={signatureStrokePath(previewSignature, liveStroke)}
              fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" /> : null}
          </svg>
        </div>
        <div role="img" aria-label="Typed signature preview" aria-hidden={signatureMode !== 'typed'}
          data-testid="typed-signature-preview" data-active={signatureMode === 'typed'}
          className="structured-signature-panel structured-signature-typed overflow-hidden rounded-lg border border-border bg-background">
          <span aria-hidden="true" className="structured-signature-mark">{signatureName || '\u00a0'}</span>
        </div>
      </div>
      <label className="grid min-w-0 gap-1 text-sm text-muted-foreground">Full name
        <input ref={typedSignatureInput} name={`${field.fieldId}-signature-name`} aria-label="Full name"
          autoComplete="name" maxLength={500} className={input} disabled={disabled} value={signatureName}
          onChange={event => updateSignatureName(event.target.value)} />
      </label>
      {signatureMode === 'drawn' && (signatureLimitReached || strokes.length >= 64 || storedPointCount >= 4096)
        ? <p role="status" className="text-sm text-muted-foreground">Signature limit reached</p>
        : null}
      <div className="flex flex-wrap gap-2">
        {submitButton(signatureMode === 'typed' ? Boolean(validTypedSignature)
          : Boolean(validDrawnSignature), submitLabel,
        signatureMode === 'typed'
          ? validTypedSignature
          : validDrawnSignature)}
        <button type="button" aria-label="Undo stroke" aria-hidden={signatureMode !== 'drawn'} tabIndex={signatureMode === 'drawn' ? 0 : -1}
          className={`${compactIconAction} structured-signature-reserved-action`}
          disabled={disabled || signatureMode !== 'drawn' || !drawnSignature?.strokes.length} onClick={undo}>
          <IconRotateClockwise aria-hidden="true" className="mx-auto h-4 w-4 -scale-x-100" />
        </button>
        <button type="button" className={compactAction}
          disabled={disabled || (signatureMode === 'typed' ? !signatureName : !drawnSignature)} onClick={clear}>Clear</button>
      </div>
    </div>;
  }

  if (field.type === 'image_click_map') {
    const current = isClickMap(draft) ? draft : { points: [] };
    return <ImageClickMapControl field={field} value={current} disabled={disabled} submitLabel={submitLabel}
      onChange={update} onSubmit={onSubmit} />;
  }

  return null;
}
